<template>
  <div
    class="empty-state"
    data-testid="stream-insights-chart-empty-state"
  >
    <div
      class="empty-state__image"
      data-testid="stream-insights-chart-empty-state-img-container"
      :style="imageStyle"
    >
      <img
        alt=""
        data-testid="stream-insights-chart-empty-state-img"
        :src="image"
      />
    </div>
    <div data-testid="stream-insights-chart-empty-state-description">
      {{ description }}
    </div>
    <div v-if="showButton">
      <BaseButton
        bordered
        data-testid="stream-insights-chart-empty-state-button"
        v-bind="buttonAttrs"
        :disabled="isButtonDisabled"
        new-design
        @click="onButtonClicked"
      />
      <BaseStreemTooltip v-if="buttonTooltip">
        {{ buttonTooltip }}
      </BaseStreemTooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type CSSProperties } from "vue";

import { BaseButton, BaseStreemTooltip } from "shared/components/base";
import type {
  ChartConfig,
  ChartEmptyState,
} from "shared/modules/Charts/types/chartConfig";

const props = defineProps<{
  chartConfig: ChartConfig;
  chartDetail?: boolean;
  hideButton?: boolean;
}>();

const emit = defineEmits<{
  "empty-clicked": [];
}>();

const description = computed<string>(
  () => props.chartConfig.emptyState?.description ?? ""
);

const isButtonDisabled = computed<boolean>(
  () => props.chartConfig.emptyState?.isButtonDisabled?.() ?? false
);

const buttonTooltip = computed<string>(
  () => props.chartConfig.emptyState?.buttonTooltip?.() ?? ""
);

const image = computed<string>(() => props.chartConfig.emptyState?.image ?? "");

const buttonAttrs = computed<ChartEmptyState["button"]>(
  () =>
    ({
      ...(props.chartConfig.emptyState?.button ?? {}),
    }) as ChartEmptyState["button"]
);

const imageStyle = computed<CSSProperties>(
  () => props.chartConfig.emptyState?.style || {}
);

const showButton = computed<boolean>(() => {
  if (props.hideButton) return false;

  if (props.chartDetail) {
    if (
      [
        "coverage_volume_and_analytics",
        "coverage_volume_and_referral_traffic",
      ].includes(props.chartConfig.method)
    ) {
      return false;
    }
  }

  return true;
});

function onButtonClicked(): void {
  if (buttonAttrs.value?.to) {
    return;
  }

  emit("empty-clicked");
}
</script>

<style lang="scss" scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  gap: var(--s-spacing-lg);
  align-items: center;
  padding-block: var(--s-spacing-xl);

  &__image {
    width: 70%;

    img {
      width: 100%;
    }
  }
}

:deep(.base-button) {
  --c-button-default: transparent;
  --c-button-hover: transparent;
  --c-button-active: transparent;
  --c-button-border-default: var(--s-button-bg-primary-default);
  --c-button-border-hover: var(--s-button-bg-primary-hover);
  --c-button-border-active: var(--s-button-bg-primary-active);
}
</style>
